import React from 'react';
import { Package, Crown, Calendar, RefreshCw, ArrowRight, ShieldCheck, Clock } from 'lucide-react';
import { UserProfile, ShipmentOrder } from '../types';

interface ProFreeBoxesCounterBarProps {
  currentUser?: UserProfile;
  shipments: ShipmentOrder[];
  onNavigateToPlans?: () => void;
  className?: string;
  compact?: boolean;
}

/**
 * Calculates the current 1-month billing cycle window [cycleStart, cycleEnd]
 * based on the user's subscription start date (or fallback creation date).
 */
function getSubscriptionBillingCycle(subscriptionStartDate?: string, fallbackCreatedAt?: string) {
  const now = new Date();
  
  // Parse anchor date
  let anchor = subscriptionStartDate 
    ? new Date(subscriptionStartDate) 
    : (fallbackCreatedAt ? new Date(fallbackCreatedAt) : new Date(now.getFullYear(), now.getMonth(), 1));
  
  if (isNaN(anchor.getTime())) {
    anchor = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  // Iterate forward from anchor date month-by-month to find current cycle
  let cycleStart = new Date(anchor);
  
  if (cycleStart <= now) {
    while (true) {
      const nextCycle = new Date(cycleStart);
      nextCycle.setMonth(nextCycle.getMonth() + 1);
      if (now >= nextCycle) {
        cycleStart = nextCycle;
      } else {
        break;
      }
    }
  }

  const cycleEnd = new Date(cycleStart);
  cycleEnd.setMonth(cycleEnd.getMonth() + 1);

  return { cycleStart, cycleEnd, anchorDate: anchor };
}

export const ProFreeBoxesCounterBar: React.FC<ProFreeBoxesCounterBarProps> = ({
  currentUser,
  shipments,
  onNavigateToPlans,
  className = '',
  compact = false
}) => {
  const plan = currentUser?.subscriptionPlan || 'free';
  const isPro = plan === 'pro';

  // Calculate 1-month cycle window based on subscription start date
  const { cycleStart, cycleEnd } = getSubscriptionBillingCycle(
    currentUser?.subscriptionStartDate,
    currentUser?.createdAt
  );

  // Format cycle range string (e.g. "15/07 a 15/08")
  const formatDateShort = (d: Date) => {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  };

  const cycleStartFormatted = formatDateShort(cycleStart);
  const cycleEndFormatted = formatDateShort(cycleEnd);
  const cycleRangeText = `${cycleStartFormatted} a ${cycleEndFormatted}`;

  // Format full reset date string (e.g. "15 de Agosto")
  const resetDateDay = cycleEnd.getDate();
  const resetDateMonth = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(cycleEnd);
  const resetDateMonthCapitalized = resetDateMonth.charAt(0).toUpperCase() + resetDateMonth.slice(1);
  const formattedResetDate = `${resetDateDay} de ${resetDateMonthCapitalized}`;

  // Filter user's shipments created within the current cycle window
  const shipmentsInCurrentCycle = shipments.filter(s => {
    if (!s.createdAt) return false;
    const createdDate = new Date(s.createdAt);
    if (isNaN(createdDate.getTime())) return false;
    return createdDate >= cycleStart && createdDate < cycleEnd;
  });

  const usedCount = shipmentsInCurrentCycle.length;
  const TOTAL_PRO_FREE_BOXES = 5;
  const remainingCount = Math.max(0, TOTAL_PRO_FREE_BOXES - usedCount);
  const progressPercent = Math.min(100, Math.round((usedCount / TOTAL_PRO_FREE_BOXES) * 100));

  // If not PRO, show an upgrade callout or hide depending on usage
  if (!isPro) {
    if (compact) return null;

    return (
      <div className={`bg-gradient-to-r from-slate-900 via-slate-800 to-red-950/40 border border-slate-700/80 rounded-2xl p-4 sm:p-5 text-white shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-red-600 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-md">
            <Crown className="w-5 h-5 text-amber-300 fill-amber-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-black text-sm text-white">Quer 5 Caixas Gratuitas por Mês?</span>
              <span className="text-[10px] bg-red-500/30 text-red-300 border border-red-500/40 px-2 py-0.5 rounded-full font-extrabold uppercase">Plano VIP PRO</span>
            </div>
            <p className="text-xs text-slate-300 mt-0.5">
              Assine o plano PRO por R$ 230/mês e ganhe isenção de taxa em até 5 consolidações a cada ciclo de 30 dias!
            </p>
          </div>
        </div>

        {onNavigateToPlans && (
          <button
            onClick={onNavigateToPlans}
            className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 text-white font-extrabold text-xs transition-all shadow-md flex items-center justify-center gap-2 shrink-0 cursor-pointer"
          >
            <span>Ver Planos VIP</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // COMPACT MODE FOR HEADERS / TABLES
  if (compact) {
    return (
      <div className={`bg-gradient-to-r from-red-950/90 via-slate-900 to-slate-900 border border-red-500/40 rounded-xl p-3 text-white shadow-xs space-y-2 ${className}`}>
        <div className="flex items-center justify-between text-xs font-bold">
          <span className="flex items-center gap-1.5 text-amber-300 font-extrabold">
            <Crown className="w-4 h-4 fill-amber-300 text-amber-300 shrink-0" />
            <span>Cota PRO: {remainingCount} de {TOTAL_PRO_FREE_BOXES} Caixas Grátis</span>
          </span>
          <span className="text-[10px] text-slate-400 font-mono">
            {usedCount}/{TOTAL_PRO_FREE_BOXES} Usadas (Ciclo: {cycleRangeText})
          </span>
        </div>
        <div className="w-full bg-slate-800 h-2.5 rounded-full overflow-hidden border border-slate-700/80 p-0.5">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${
              usedCount >= TOTAL_PRO_FREE_BOXES 
                ? 'bg-red-500' 
                : usedCount >= 3 
                ? 'bg-amber-400' 
                : 'bg-gradient-to-r from-emerald-500 to-teal-400'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    );
  }

  // FULL PRO BANNER MODE
  return (
    <div className={`bg-gradient-to-br from-slate-900 via-slate-900 to-red-950 border-2 border-red-500/50 rounded-2xl p-4 sm:p-6 text-white shadow-xl space-y-4 relative overflow-hidden ${className}`}>
      {/* Background Subtle Pattern */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full blur-3xl pointer-events-none" />
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-red-600 via-orange-500 to-amber-500 text-white flex items-center justify-center shrink-0 shadow-lg ring-2 ring-amber-400/30 animate-pulse">
            <Crown className="w-5 h-5 text-amber-200 fill-amber-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base sm:text-lg font-black text-white tracking-tight">
                Cota de Caixas Gratuitas - Plano VIP PRO
              </h3>
              <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-extrabold uppercase">
                Ativo
              </span>
            </div>
            <p className="text-xs text-slate-300 font-medium">
              Acompanhamento do período mensal individual a partir da data de assinatura.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-xl text-xs font-mono text-amber-300 font-extrabold flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-amber-400" />
            <span>Ciclo Atual: {cycleRangeText}</span>
          </span>
        </div>
      </div>

      {/* Main Counter & Progress Bar */}
      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm font-bold">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-red-400" />
            <span className="text-slate-200">
              Caixas Gratuitas Disponíveis no Ciclo:
            </span>
            <span className={`text-sm sm:text-base font-black px-2.5 py-0.5 rounded-lg border font-mono ${
              remainingCount > 0 
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' 
                : 'bg-red-500/20 text-red-300 border-red-500/40'
            }`}>
              {remainingCount} de {TOTAL_PRO_FREE_BOXES} restantes
            </span>
          </div>

          <span className="text-xs text-slate-400 font-mono">
            {usedCount} de {TOTAL_PRO_FREE_BOXES} caixas enviadas ({progressPercent}%)
          </span>
        </div>

        {/* Progress Bar Container */}
        <div className="w-full bg-slate-800/90 h-4 rounded-full overflow-hidden p-0.5 border border-slate-700/80 shadow-inner relative">
          <div 
            className={`h-full rounded-full transition-all duration-700 relative overflow-hidden ${
              usedCount >= TOTAL_PRO_FREE_BOXES
                ? 'bg-gradient-to-r from-red-600 to-orange-600'
                : usedCount >= 3
                ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                : 'bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-400'
            }`}
            style={{ width: `${progressPercent}%` }}
          >
            {/* Glossy overlay effect */}
            <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.3)_50%,transparent_100%)] animate-shimmer" />
          </div>
        </div>

        {/* Progress Markers Grid */}
        <div className="grid grid-cols-5 gap-1 pt-1 text-center text-[10px] font-mono text-slate-400 font-bold">
          {[1, 2, 3, 4, 5].map((boxNum) => {
            const isUsed = usedCount >= boxNum;
            return (
              <div 
                key={boxNum} 
                className={`py-1 rounded-lg border transition-all ${
                  isUsed 
                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' 
                    : 'bg-slate-800/60 border-slate-700 text-slate-400'
                }`}
              >
                Caixa #{boxNum} {isUsed ? '✓ Enviada' : '🎁 Livre'}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Info & Reset Date Notice */}
      <div className="pt-2 flex flex-wrap items-center justify-between gap-3 text-xs border-t border-slate-800/80 text-slate-300">
        <div className="flex items-center gap-1.5 text-amber-300 font-medium">
          <RefreshCw className="w-3.5 h-3.5 text-amber-400 animate-spin-slow shrink-0" />
          <span>
            <strong>Reset Automático no Ciclo de Assinatura:</strong> A sua cota de 5 caixas é válida de <strong>{cycleRangeText}</strong> e será renovada automaticamente em <strong>{formattedResetDate}</strong>.
          </span>
        </div>

        {onNavigateToPlans && (
          <button
            onClick={onNavigateToPlans}
            className="text-xs font-bold text-slate-300 hover:text-white underline decoration-slate-600 hover:decoration-white transition-colors cursor-pointer"
          >
            Gerenciar Assinatura
          </button>
        )}
      </div>
    </div>
  );
};

export default ProFreeBoxesCounterBar;
